import {
  Button,
  Combobox,
  Dialog,
  Flex,
  useToasts,
  Text,
  Spinner,
  MenuButton,
  TextInput,
  TextInputProps,
  createForm,
} from '@applyboard/crystal-ui';
import {
  DeleteOutlineIcon,
  EditOutlineIcon,
  EmailOutlineIcon,
  MoreVertOutlineIcon,
  PlusOutlineIcon,
} from '@applyboard/ui-icons';
import { ComponentType, useState } from 'react';
import { useErrorHandler } from '~/hooks';
import { getApiErrorMessage } from '~/utils/getApiErrorMessage';
import { useAppProcessing } from '../../context';
import { useChangeStudent } from '../../hooks/useChangeStudent';
import { usePatchStudentEmail } from '~/pages/ApplicationDetails/data/usePatchStudentEmail';
import { useStudentProfiles, StudentProfile } from '../../data/useStudentProfiles';
import { LabelValue } from '~/components/uie-fix/LabelValue';

export function ChangeStudent() {
  const { application } = useAppProcessing();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isStudentAssignmentOpen, setIsStudentAssignmentOpen] = useState(false);
  const [isStudentRemovalOpen, setIsStudentRemovalOpen] = useState(false);
  const [isStudentEmailOpen, setIsStudentEmailOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState('');
  const { changeStudent, isPatchingStudentAssignment } = useChangeStudent(application.id);
  const { patchStudentEmail, isPatchingStudentEmail } = usePatchStudentEmail();
  const { profiles, isFetchingProfiles, isLoadingProfiles } = useStudentProfiles({ enabled: isStudentAssignmentOpen });
  const isProfilesLoading = isFetchingProfiles || isLoadingProfiles;
  const toast = useToasts();
  const { apiError } = useErrorHandler();

  const onSave = async () => {
    try {
      const selectedStudentDetails = profiles.find((profile: StudentProfile) => profile.id === selectedStudent);
      if (!selectedStudentDetails) {
        apiError();
        return;
      }
      await changeStudent({
        studentAccountExternalId: selectedStudentDetails.id,
      });
      toast.positive('Account owner has been successfully changed.');
      setSelectedStudent('');
      setIsStudentAssignmentOpen(false);
      setTimeout(() => {
        window.location.href = `/applications/${application.id}`;
      }, 1000);
    } catch (err) {
      apiError();
    }
  };

  const onRemove = async () => {
    try {
      await changeStudent({
        studentAccountExternalId: '',
      });
      toast.positive('Account owner has been removed.');
      setIsStudentRemovalOpen(false);
      setTimeout(() => {
        window.location.href = `/applications/${application.id}`;
      }, 1000);
    } catch (err) {
      apiError();
    }
  };

  const onUpdateEmail = async (newEmail: string) => {
    if (!application.studentProfile) {
      apiError();
      return;
    }
    try {
      await patchStudentEmail({
        profileId: application.studentProfile.id,
        email: newEmail,
      });
      toast.positive('Account email has been updated.');
      setIsStudentEmailOpen(false);
      setTimeout(() => {
        window.location.href = `/applications/${application.id}`;
      }, 1000);
    } catch (err) {
      apiError(getApiErrorMessage(err));
    }
  };

  return (
    <>
      <Flex gap={2}>
        {application.studentProfile ? (
          <MenuButton
            alignment="right"
            onOpenChange={(value) => {
              setIsMenuOpen(value);
            }}
            open={isMenuOpen}
          >
            <MenuButton.Trigger>
              <Button
                emphasis="transparent"
                aria-label="Change account owner"
                leadIcon={MoreVertOutlineIcon}
                onClick={() => setIsMenuOpen((prev) => !prev)}
              />
            </MenuButton.Trigger>
            <MenuButton.Button
              icon={EditOutlineIcon}
              id="change-student-assignment"
              onClick={() => setIsStudentAssignmentOpen(true)}
            >
              Change account owner
            </MenuButton.Button>
            <MenuButton.Button
              icon={EmailOutlineIcon}
              id="update-student-email"
              onClick={() => setIsStudentEmailOpen(true)}
            >
              Update account email
            </MenuButton.Button>
            <MenuButton.Button
              icon={DeleteOutlineIcon}
              id="remove-student"
              onClick={() => setIsStudentRemovalOpen(true)}
            >
              Unassign account
            </MenuButton.Button>
          </MenuButton>
        ) : (
          <Button
            intent="primary"
            emphasis="transparent"
            aria-label="Assign account owner"
            onClick={() => setIsStudentAssignmentOpen(true)}
            leadIcon={PlusOutlineIcon}
          />
        )}
      </Flex>

      <Dialog
        open={isStudentAssignmentOpen}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            setSelectedStudent('');
            setIsStudentAssignmentOpen(false);
          }
        }}
      >
        <Dialog.Heading>Change account owner</Dialog.Heading>
        <Dialog.Content>
          <Flex gap={6} direction="column">
            {application.studentProfile && (
              <LabelValue label="CURRENT Account" value={formatStudentProfileLabel(application.studentProfile)} />
            )}
            {isProfilesLoading ? (
              <Flex align="center" gap={2}>
                <Spinner size="sm" />
                <Text variant="bodyM">Loading account owners...</Text>
              </Flex>
            ) : (
              <>
                <SelectStudent
                  profiles={profiles}
                  selectedStudent={selectedStudent}
                  setSelectedStudent={setSelectedStudent}
                />
                <Text variant="bodyS" contrast="low">
                  Once you assign an account, they will see this application in their portal
                </Text>
              </>
            )}
          </Flex>
        </Dialog.Content>
        <Dialog.Actions>
          <Dialog.CloseButton width="fill">Cancel</Dialog.CloseButton>
          <Button
            intent="primary"
            width="fill"
            disabled={!selectedStudent || isPatchingStudentAssignment || isProfilesLoading}
            onClick={() => {
              onSave();
            }}
            loading={isPatchingStudentAssignment}
          >
            Confirm
          </Button>
        </Dialog.Actions>
      </Dialog>

      <Dialog
        open={isStudentRemovalOpen}
        onOpenChange={(isOpen) => {
          if (isPatchingStudentAssignment) return;
          if (!isOpen) setIsStudentRemovalOpen(false);
        }}
      >
        <Dialog.Heading>Remove account owner</Dialog.Heading>
        <Dialog.Content>
          <Flex gap={4} direction="column">
            {application.studentProfile && (
              <LabelValue label="Current account owner" value={formatStudentProfileLabel(application.studentProfile)} />
            )}
            <Text variant="bodyM">This will remove application access for the current account owner.</Text>
          </Flex>
        </Dialog.Content>
        <Dialog.Actions>
          <Dialog.CloseButton width="fill" disabled={isPatchingStudentAssignment}>
            Cancel
          </Dialog.CloseButton>
          <Button
            intent="negative"
            width="fill"
            onClick={onRemove}
            loading={isPatchingStudentAssignment}
            disabled={isPatchingStudentAssignment}
          >
            Remove
          </Button>
        </Dialog.Actions>
      </Dialog>

      <Dialog
        open={isStudentEmailOpen}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            setIsStudentEmailOpen(false);
          }
        }}
      >
        <Dialog.Heading>Update account email</Dialog.Heading>
        <Dialog.Content>
          <UpdateEmailForm
            defaultValues={{ email: '' }}
            onSubmit={(data) => onUpdateEmail(data.email)}
            id="update-student-email-form"
            key={isStudentEmailOpen ? 'open' : 'closed'}
          >
            <Flex gap={4} direction="column">
              {application.studentProfile && (
                <LabelValue label="Current email" value={application.studentProfile.email} />
              )}
              <UpdateEmailField
                as={TextInput as ComponentType<TextInputProps>}
                label="New email"
                name="email"
                type="email"
                validate={(value) => {
                  if (!value.trim()) {
                    return 'Email is required.';
                  }
                  if (!emailValidate(value)) {
                    return 'Please enter a valid email address.';
                  }
                  return true;
                }}
              />
            </Flex>
          </UpdateEmailForm>
        </Dialog.Content>
        <Dialog.Actions>
          <Dialog.CloseButton width="fill" disabled={isPatchingStudentEmail}>
            Cancel
          </Dialog.CloseButton>
          <Button
            intent="primary"
            width="fill"
            type="submit"
            form="update-student-email-form"
            loading={isPatchingStudentEmail}
            disabled={isPatchingStudentEmail}
          >
            Update email
          </Button>
        </Dialog.Actions>
      </Dialog>
    </>
  );
}

interface SelectStudentProps {
  profiles: StudentProfile[];
  selectedStudent: string;
  setSelectedStudent: React.Dispatch<React.SetStateAction<string>>;
}

const SelectStudent = ({ profiles, selectedStudent, setSelectedStudent }: SelectStudentProps) => {
  return (
    <Combobox
      label="New account owner"
      required
      onChange={(value) => setSelectedStudent(value)}
      value={selectedStudent}
      aria-label="account owner"
      type="search"
      placeholder="Search for an account owner"
      filterOption={(option, query) => option.label.toLowerCase().includes(query.toLowerCase())}
    >
      {profiles.length > 0
        ? profiles.map((profile) => (
            <Combobox.Option key={profile.id} label={formatStudentLabel(profile)} value={profile.id} />
          ))
        : null}
    </Combobox>
  );
};

const formatStudentLabel = (profile: StudentProfile) => {
  const names = [profile.givenName, profile.familyName].filter(Boolean).join(' ');
  return `${names || profile.email} (${profile.email})`;
};

const formatStudentProfileLabel = (profile: { givenName: string; familyName?: string; email: string }) => {
  const names = [profile.givenName, profile.familyName].filter(Boolean).join(' ');
  return names ? `${names} (${profile.email})` : profile.email;
};

const { Form: UpdateEmailForm, Field: UpdateEmailField } = createForm<{
  email: string;
}>();

const emailValidate = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
